import * as React from 'react'

/**
 * Counts React nodes that would result in an empty render.
 *
 * @param node the node to check.
 * @param breakOnFirstFoundChild if `true`, stops counting after the first child is found. Default is false.
 */
export function nonEmptyChildrenCount(
  node: React.ReactNode,
  breakOnFirstFoundChild = false,
) {
  let count = 0
  let broke = false
  React.Children.forEach(node, (c) => {
    if (broke) {
      return
    }

    if (
      c === null ||
      c === undefined ||
      c === false ||
      c === '' ||
      (Array.isArray(c) && c.length === 0)
    ) {
      return
    }

    // Check fragments, all other node types would result in output.
    if ((c as any).type === React.Fragment) {
      // This might seem terrible, but in reality it never goes that deep, and it happens
      // very rarely.
      if (
        nonEmptyChildrenCount(
          (c as any).props?.children,
          breakOnFirstFoundChild,
        ) === 0
      ) {
        return
      }
    }

    count++
    if (breakOnFirstFoundChild) {
      broke = true
    }
  })

  return count
}

/**
 * Check if children are going to result in an empty render,
 * this is faster than getting the full child count.
 *
 * @param node
 */
export function isChildrenEmpty(node: React.ReactNode | undefined) {
  return nonEmptyChildrenCount(node, true) === 0
}

/**
 * Merges React refs.
 * @param refs
 */
export function mergeRefs<T>(
  refs: (React.Ref<T> | React.RefObject<T> | null | undefined)[],
): React.RefCallback<T> {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(value)
      } else if (ref !== null && ref !== undefined) {
        ;(ref as any).current = value
      }
    })
  }
}

/**
 * Callback that returns the latest value.
 * @param val
 */
export function useGetLatest<T>(val: T) {
  const ref = React.useRef<T>(val)
  ref.current = val
  return React.useCallback(() => ref.current, [])
}

/**
 * Creates a function that returns a DOMRect.
 * @param x
 * @param y
 */
export function generateBoundingClientRect(x = 0, y = 0): () => DOMRect {
  return () => ({
    width: 0,
    height: 0,
    top: y,
    right: x,
    bottom: y,
    left: x,
    x: 0,
    y: 0,
    toJSON: () => null,
  })
}
